import { useState, useEffect, useRef, useCallback } from "react"
import useWorldStore from "../store/worldStore"
import { sendAgentMessage } from "../services/agentChat"
import { findWorldAgentById } from "../lib/agentIdentity.mjs"
import useFloatingWindowLayer from "../hooks/useFloatingWindowLayer"
import {
  shouldSkipPointerDragStart,
  startPointerSession,
} from "../../../lib/pointer-drag"

const mono = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" }

function MessageBubble({ msg, color }) {
  const isUser = msg.role === "user"
  const isSystem = msg.role === "system"

  if (isSystem) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "3px 0" }}
      >
        <span
          style={{ ...mono, fontSize: 10, color: "rgba(255,255,255,0.35)" }}
        >
          {msg.text}
        </span>
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: 6,
      }}
    >
      <div
        style={{
          maxWidth: "80%",
          padding: "6px 10px",
          borderRadius: 10,
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 12,
          lineHeight: 1.4,
          color: isUser ? color : "rgba(255,255,255,0.8)",
          background: isUser ? `${color}33` : "rgba(255,255,255,0.05)",
          border: isUser ? `1px solid ${color}66` : "none",
        }}
      >
        {msg.text}
      </div>
    </div>
  )
}

function ChatWindow({ chat, agent, index }) {
  const [input, setInput] = useState("")
  const chatEndRef = useRef()
  const minimizeChat = useWorldStore((s) => s.minimizeChat)
  const closeChat = useWorldStore((s) => s.closeChat)
  const addMessage = useWorldStore((s) => s.addMessage)
  const setTyping = useWorldStore((s) => s.setTyping)
  const activeToolUse = useWorldStore((s) => s.activeToolUse)
  const optimisticToolUse = useWorldStore((s) => s.optimisticToolUse)
  const canonicalAgentId = agent?.id || chat.agentId
  const toolUse =
    optimisticToolUse[canonicalAgentId] ||
    activeToolUse[canonicalAgentId] ||
    optimisticToolUse[chat.agentId] ||
    activeToolUse[chat.agentId]
  const color = agent?.color || "#888"
  const windowId = `text-chat:${chat.agentId}`
  const { zIndex, focusProps, focusWindow } = useFloatingWindowLayer(windowId)

  // Per-window position and size
  const [pos, setPos] = useState({
    x: window.innerWidth - 360 - index * 20,
    y: window.innerHeight - 460 - index * 20,
  })
  const [size, setSize] = useState({ w: 340, h: 400 })

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [chat.messages.length, chat.isTyping, toolUse])

  const sendMessage = async () => {
    const text = input.trim()
    if (!text || chat.isTyping) return
    addMessage(chat.agentId, { role: "user", text, ts: Date.now() })
    setInput("")
    setTyping(chat.agentId, true)

    try {
      const history = chat.messages.filter(
        (m) => m.role === "user" || m.role === "agent"
      )
      const response = await sendAgentMessage(chat.agentId, text, history)
      setTyping(chat.agentId, false)
      addMessage(chat.agentId, {
        role: "agent",
        text: response,
        ts: Date.now(),
      })
    } catch {
      setTyping(chat.agentId, false)
      addMessage(chat.agentId, {
        role: "agent",
        text: "Agent unavailable.",
        ts: Date.now(),
      })
    }
  }

  // Drag handler
  const onDragStart = useCallback(
    (e) => {
      if (shouldSkipPointerDragStart(e)) return
      focusWindow()

      const startX = e.clientX,
        startY = e.clientY
      const startPos = { ...pos }

      startPointerSession(e, {
        onMove: (ev) => {
          setPos({
            x: Math.max(
              0,
              Math.min(
                window.innerWidth - 100,
                startPos.x + ev.clientX - startX
              )
            ),
            y: Math.max(
              40,
              Math.min(
                window.innerHeight - 50,
                startPos.y + ev.clientY - startY
              )
            ),
          })
        },
      })
    },
    [focusWindow, pos]
  )

  // Resize handler
  const onResizeStart = useCallback(
    (e) => {
      e.stopPropagation()
      const startX = e.clientX,
        startY = e.clientY
      const startW = size.w,
        startH = size.h

      startPointerSession(e, {
        onMove: (ev) => {
          setSize({
            w: Math.max(260, startW + ev.clientX - startX),
            h: Math.max(200, startH + ev.clientY - startY),
          })
        },
      })
    },
    [size]
  )

  // Minimized pill drag
  const onPillPointerDown = useCallback(
    (e) => {
      if (shouldSkipPointerDragStart(e)) return
      focusWindow()

      const startX = e.clientX,
        startY = e.clientY
      const startPos = { ...pos }
      let didMove = false

      startPointerSession(e, {
        onMove: (ev) => {
          const dx = ev.clientX - startX,
            dy = ev.clientY - startY
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didMove = true
          setPos({
            x: Math.max(0, Math.min(window.innerWidth - 100, startPos.x + dx)),
            y: Math.max(40, Math.min(window.innerHeight - 30, startPos.y + dy)),
          })
        },
        onEnd: (reason) => {
          if (reason === "end" && !didMove) minimizeChat(chat.agentId)
        },
      })
    },
    [focusWindow, pos, chat.agentId, minimizeChat]
  )

  if (chat.minimized) {
    return (
      <div
        onPointerDown={onPillPointerDown}
        {...focusProps}
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          background: "rgba(6,6,7,0.95)",
          backdropFilter: "blur(20px)",
          border: `1px solid ${color}66`,
          borderRadius: 20,
          padding: "6px 14px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: "grab",
          zIndex,
          userSelect: "none",
          touchAction: "none",
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 6px ${color}88`,
          }}
        />
        <span style={{ ...mono, fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
          {agent?.name || "Agent"}
        </span>
      </div>
    )
  }

  return (
    <div
      {...focusProps}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        background: "rgba(6,6,7,0.95)",
        backdropFilter: "blur(20px)",
        border: `1px solid ${color}40`,
        borderRadius: 10,
        zIndex,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
      }}
    >
      {/* Header — drag handle */}
      <div
        onPointerDown={onDragStart}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "6px 10px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          gap: 8,
          flexShrink: 0,
          cursor: "grab",
          userSelect: "none",
          touchAction: "none",
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 6px ${color}88`,
          }}
        />
        <span style={{ ...mono, fontSize: 12, color: "white", flex: 1 }}>
          {agent?.name || "Agent"}
        </span>
        <button
          onClick={() => minimizeChat(chat.agentId)}
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.4)",
            fontSize: 14,
            cursor: "pointer",
            ...mono,
            padding: "0 3px",
          }}
        >
          −
        </button>
        <button
          onClick={() => closeChat(chat.agentId)}
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.4)",
            fontSize: 14,
            cursor: "pointer",
            ...mono,
            padding: "0 3px",
          }}
        >
          ×
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
        {chat.messages.map((msg, i) => (
          <MessageBubble key={i} msg={msg} color={color} />
        ))}
        {(chat.isTyping || toolUse) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 0",
            }}
          >
            {toolUse ? (
              <span style={{ ...mono, fontSize: 9, color }}>
                {toolUse.toolEmoji} Using {toolUse.toolLabel}...
              </span>
            ) : (
              <>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: color,
                      animation: `typingDot 1.2s ease-in-out infinite`,
                      animationDelay: `${i * 0.2}s`,
                    }}
                  />
                ))}
              </>
            )}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div
        style={{ padding: "6px 10px", display: "flex", gap: 6, flexShrink: 0 }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Type a message..."
          style={{
            flex: 1,
            height: 30,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 6,
            padding: "0 10px",
            fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
            fontSize: 12,
            color: "white",
            outline: "none",
          }}
        />
        <button
          onClick={sendMessage}
          style={{
            width: 30,
            height: 30,
            background: `${color}26`,
            border: `1px solid ${color}66`,
            borderRadius: 6,
            color,
            fontSize: 14,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ↑
        </button>
      </div>

      {/* Resize handle */}
      <div
        onPointerDown={onResizeStart}
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 14,
          height: 14,
          cursor: "nwse-resize",
          touchAction: "none",
        }}
      />

      <style>{`
        @keyframes typingDot {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-2px); }
        }
      `}</style>
    </div>
  )
}

export default function ChatWidget() {
  const openChats = useWorldStore((s) => s.openChats)
  const agents = useWorldStore((s) => s.agents)

  const visibleChats = openChats.filter((c) => !c.closed)
  if (visibleChats.length === 0) return null

  return (
    <>
      {visibleChats.map((chat, i) => {
        const agent = findWorldAgentById(chat.agentId, agents)
        return (
          <ChatWindow key={chat.agentId} chat={chat} agent={agent} index={i} />
        )
      })}
    </>
  )
}
